import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

function detailToText(d) {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
}

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw1 !== pw2) return setError("Passwords do not match");
    if (pw1.length < 10) return setError("New password must be at least 10 characters");
    setBusy(true);
    try {
      await api.post("/admin-recovery/complete-reset", { token, new_password: pw1 });
      setDone(true);
    } catch (err) {
      setError(detailToText(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-xl" data-testid="admin-reset-card">
        <BrandLogo />
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-secondary">Set a new password</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          For the admin account <span className="font-semibold">admin@tezzkhana.com</span>. Minimum 10 characters.
        </p>

        {!token && !done && (
          <div className="mt-6 rounded-xl border border-border/70 bg-muted/40 p-4 text-sm text-secondary/80" data-testid="admin-reset-no-token">
            This page needs the reset link from your email. Request one on the{" "}
            <Link to="/admin/forgot-password" className="font-semibold text-primary hover:underline">forgot password</Link> page.
          </div>
        )}

        {done ? (
          <div className="mt-6 space-y-4" data-testid="admin-reset-success">
            <div className="flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm font-semibold text-secondary">
              <ShieldCheck className="h-5 w-5 text-primary" /> Password reset successfully.
            </div>
            <Link to="/admin/login" className="btn-primary inline-flex" data-testid="admin-reset-login-link">
              Go to admin sign in
            </Link>
          </div>
        ) : (
          token && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-reset-form">
              <PwField label="New password (min 10 chars)" testid="admin-reset-password-input" value={pw1} onChange={(e) => setPw1(e.target.value)} />
              <PwField label="Confirm new password" testid="admin-reset-confirm-input" value={pw2} onChange={(e) => setPw2(e.target.value)} />
              {error && (
                <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="admin-reset-error">
                  {error}
                </div>
              )}
              <button type="submit" data-testid="admin-reset-submit" className="btn-primary w-full justify-center" disabled={busy}>
                <KeyRound className="h-4 w-4" /> {busy ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
}

function PwField({ label, testid, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</label>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={onChange}
          required
          minLength={10}
          className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 pr-10 text-sm focus:border-primary focus:outline-none"
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
